import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../../components/navbar/navbarComponent";
import { getDetailSecuestro } from "../../redux/actions";
import EgresarVehiculoComponent from "./modals/egresar";
import TakeFotoToDetailComponent from "./takeFotoToDetail";
import CambiarEstadoComponent from "./modals/cambiarEstado";
import CompactarComponent from "./modals/compactar";
import { generatePdf } from "../ingreso/generatePdf";
import ModalImagenInfo from "./modals/modalInfo";
import { CameraIcon, FileUploadIcon, ActionCircleIcon, PrintQrIcon, InfoIcon } from "../../components/icons/SvgIcons";
import { formatArgDate } from "../../utils/formatDate";
import { useEstado, ESTADO } from "../../hooks/useEstado";
import { useEgresoStatus } from "../../hooks/useEgresoStatus";
import Button from "../../components/button";
import { useCompactarStatus } from "../../hooks/useCompactarStatus";
import InfoSection from "./components/infoSection";
import InfoRow from "./components/infoRow";
import EgresoSection from "./egresoSection";
import CompactarSection from "./compactarSection";
import ErrorModal from "../../components/modals/errorModal";
import CambiarSectorComponent from "./modals/cambiarSector";
import VerEgresoComponent from "./modals/verEgreso";
import UploadFotoToDetailComponent from "./uploadFotoToDetail";

const MAX_FILE_SIZE = 5 * 1024 * 1024;

function DetailComponent() {
  const detail = useSelector((state) => state.detail);
  const loading = useSelector((state) => state.loadingDetail);
  const tipoCurrentUser = useSelector((state) => state.tipoCurrentUser);
  const [error, setError] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });
  const openErrorModal = (message) => setErrorModal({ show: true, message });
  const closeErrorModal = () => setErrorModal({ show: false, message: "" });

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  useEffect(() => {
    const fetchDetalle = async () => {
      try {
        setError(false);
        await dispatch(getDetailSecuestro(id));
      } catch (error) {
        console.error("Error al cargar el detalle:", error);
        setError(true);
      }
    };

    fetchDetalle();
  }, [id, dispatch]);

  const refreshDetalle = useCallback(() => {
    dispatch(getDetailSecuestro(id));
  }, [id, dispatch]);

  const { estado, puedeCompactar } = useEstado(detail);

  // Hooks de sección: cada uno deriva su propia variante de UI
  const { variant: egresoVariant, levantamiento } = useEgresoStatus(detail, estado, ESTADO.COMPACTADO);
  const { variant: compactarVariant } = useCompactarStatus(detail, estado, ESTADO);

  const urlFoto = useMemo(() => {
    const env = process.env.REACT_APP_ENVIRONMENT || "development";
    const remoteDir =
      env === "production"
        ? "images/corralon/production/fotos"
        : "images/corralon/stage/fotos";
    return `https://staticcontent.sannicolasciudad.gob.ar/${remoteDir}/${detail.foto}`;
  }, [detail.foto]);

  const fechaHoraFormateada = useMemo(
    () => formatArgDate(detail.Acta?.fecha_hora),
    [detail.Acta?.fecha_hora]
  );

  const [imagen, setImagen] = useState(null);

  const handleFileChange = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      openErrorModal("Por favor, selecciona un archivo de imagen válido.");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      openErrorModal("La imagen es demasiado grande. Selecciona una de menos de 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => setImagen(reader.result);
  }, []);

  const handleBackClick = useCallback(() => navigate(-1), [navigate]);

  const handleImprimirQr = useCallback(async () => {
    if (isPrinting) return;
    setIsPrinting(true);
    try {
      const pdfResponse = await generatePdf({
        idSecuestro: id,
        nroActa: detail.Acta?.nro || "Sin acta",
      });
      if (pdfResponse !== "PDF generado con éxito") {
        throw new Error(pdfResponse);
      }
    } catch (error) {
      console.error("Error al generar el PDF:", error);
      openErrorModal("Hubo un error al generar el PDF. Por favor, intenta nuevamente.");
    } finally {
      setIsPrinting(false);
    }
  }, [isPrinting, id, detail.Acta?.nro]);

  const openURL = useCallback((url) => {
    if (url) window.open(url, "_blank");
  }, []);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalEgresoOpen, setIsModalEgresoOpen] = useState(false);
  const [isTakeFotoOpen, setIsTakeFotoOpen] = useState(false);
  const [isVerEgresoOpen, setIsVerEgresoOpen] = useState(false);
  const [isCambiarEstadoOpen, setIsCambiarEstadoOpen] = useState(false);
  const [compactarOpen, setCompactarOpen] = useState(false);
  const [modalInfo, setModalInfo] = useState(null);

  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);
  const openModalEgreso = useCallback(() => setIsModalEgresoOpen(true), []);
  const closeModalEgreso = useCallback(() => setIsModalEgresoOpen(false), []);
  const openModalCompactar = useCallback(() => setCompactarOpen(true), []);
  const closeModalCompactar = useCallback(() => setCompactarOpen(false), []);
  const openTakeFoto = useCallback(() => setIsTakeFotoOpen(true), []);
  const closeTakeFoto = useCallback(() => setIsTakeFotoOpen(false), []);
  const closeUploadFoto = useCallback(() => setImagen(null), []);
  const openVerEgreso = useCallback(() => setIsVerEgresoOpen(true), []);
  const closeVerEgreso = useCallback(() => setIsVerEgresoOpen(false), []);
  const openCambiarEstado = useCallback(() => setIsCambiarEstadoOpen(true), []);
  const onCloseEstado = useCallback(() => setIsCambiarEstadoOpen(false), []);
  const openImagenInfo = useCallback((caso) => setModalInfo(caso), []);
  const closeImagenInfo = useCallback(() => setModalInfo(null), []);

  const mostrarCambiarEstado =
    [ESTADO.DESCONOCIDO, ESTADO.A_COMPACTAR, ESTADO.INGRESADO, ESTADO.NO_COMPACTAR].includes(estado) &&
    !(tipoCurrentUser === "user" && [ESTADO.A_COMPACTAR, ESTADO.NO_COMPACTAR].includes(estado)) &&
    detail.egreso === null &&
    detail.compactado === null;

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="px-36 py-24 animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-4">
              <div className="h-10 bg-gray-300 rounded mb-6"></div>
              <div className="h-[500px] bg-gray-300 rounded"></div>
            </div>
            <div className="space-y-4">
              <div className="h-32 bg-gray-300 rounded"></div>
              <div className="h-32 bg-gray-300 rounded"></div>
              <div className="h-32 bg-gray-300 rounded"></div>
            </div>
            <div className="space-y-4">
              <div className="h-32 bg-gray-300 rounded"></div>
              <div className="h-32 bg-gray-300 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div>
        <Navbar />

        <div className="flex min-h-screen items-center justify-center bg-[#F5FAFF]">
          <div className="text-center">
            <h2 className="text-lg font-semibold text-gray-700">
              No se pudo cargar el detalle
            </h2>

            <p className="mt-2 text-sm text-gray-500">
              Ocurrió un problema al comunicarse con el servidor.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />

      {errorModal.show && (
        <ErrorModal
          closeErrorModal={closeErrorModal}
          message={errorModal.message}
        />
      )}

      {isModalOpen &&
        <CambiarSectorComponent
          onClose={closeModal}
          refreshDetalle={refreshDetalle}
        />
      }

      {isModalEgresoOpen && (
        <EgresarVehiculoComponent
          onCloseEgresar={closeModalEgreso}
          idLevantamiento={levantamiento?.id || null}
          refreshDetalle={refreshDetalle}
        />
      )}

      {isTakeFotoOpen && (
        <TakeFotoToDetailComponent
          id={id}
          closeTakeFoto={closeTakeFoto}
          onFotoActualizada={refreshDetalle}
        />
      )}

      {imagen && (
        <UploadFotoToDetailComponent
          id={id}
          imagen={imagen}
          closeUploadFoto={closeUploadFoto}
          onFotoActualizada={refreshDetalle}
        />
      )}

      {isVerEgresoOpen && (
        <VerEgresoComponent
          idSecuestro={id}
          closeVerEgreso={closeVerEgreso}
        />
      )}

      {isCambiarEstadoOpen && (
        <CambiarEstadoComponent
          onCloseEstado={onCloseEstado}
          puedeCompactar={puedeCompactar}
          refreshDetalle={refreshDetalle}
        />
      )}

      {compactarOpen && (
        <CompactarComponent
          idSecuestro={id}
          closeModalCompactar={closeModalCompactar}
          nroActa={detail.Acta?.nro || null}
          lugar={detail.Acta?.lugar || null}
          refreshDetalle={refreshDetalle}
        />
      )}

      {modalInfo && (
        <ModalImagenInfo
          caso={modalInfo}
          onClose={closeImagenInfo}
        />
      )}

      <div
        className={`flex flex-col items-center justify-center bg-[#F5FAFF] min-h-screen
        ${tipoCurrentUser === "viewer" ? "pointer-events-none opacity-50" : ""}`}
      >
        <div className="flex items-center max-w-[65rem] px-2.5 w-full my-5">
          <button
            onClick={handleBackClick}
            className="flex items-center justify-center p-2 rounded-full hover:drop-shadow-[3px_3px_5px_rgba(0,0,0,0.3)] transition ease-in-out duration-200"
          >
            <ActionCircleIcon className="flex flex-start h-12 drop-shadow-[2px_2px_4px_rgba(0,0,0,0.3)]" />
          </button>

          <div className="text-3xl mx-auto font-semibold text-gray-900 text-center font-inter">
            {detail.Vehiculo?.tipovh || "Sin tipo"},{" "}
            {detail.Vehiculo?.dominio || "Sin dominio"}
          </div>
        </div>

        <div className="mb-9 px-2.5 my-2 max-w-[65rem] gap-[1rem] grid grid-cols-1 mid:grid-cols-3 custom:grid-cols-2">
          {/* ---- COLUMNA FOTO ---- */}
          <div>
            <div className="flex justify-center items-center gap-[0.5rem] mb-[1rem]">
              <button
                onClick={handleImprimirQr}
                disabled={isPrinting}
                aria-label="Imprimir código QR"
                className={`w-full h-[45px] px-[4px] py-[6px] rounded-[8px] flex items-center justify-center text-[#F6F5F5] text-[16px] font-inter font-semibold gap-[4px]
                  ${isPrinting ? "bg-gray-400 cursor-not-allowed" : "bg-[#0477AD]"}`}
              >
                {isPrinting ? "Generando..." : "Imprimir QR"}
                <PrintQrIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="items-center flex justify-center">
              {detail.foto ? (
                <img
                  className="object-cover h-[500px] w-full rounded-[12px]"
                  src={urlFoto}
                  alt=""
                />
              ) : (
                <div className="justify-center flex flex-col items-center bg-white object-cover border border-[#61ABCF] h-[500px] w-full rounded-[12px] gap-10">
                  <div className="text-[3rem] text-[#3d4245] font-bold font-inter text-center">
                    Sin foto
                  </div>
                  <div className="flex flex-col gap-6">
                    <Button
                      size="sm"
                      onClick={openTakeFoto}
                      icon={<CameraIcon className="size-6" />}
                      className="h-[45px] px-[12px] py-[6px] rounded-[8px] text-[16px]"
                    >
                      Tomar foto
                    </Button>

                    <label
                      htmlFor="file-upload"
                      className="h-[45px] px-[12px] py-[6px] bg-[#0477AD] rounded-[8px] overflow-hidden justify-center items-center flex text-[#F6F5F5] text-[13px] font-inter font-semibold gap-[4px] cursor-pointer"
                    >
                      <FileUploadIcon className="size-6" />
                      Subir foto
                      <input
                        type="file"
                        id="file-upload"
                        className="hidden"
                        onChange={handleFileChange}
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ---- COLUMNA CENTRAL: secuestro, egreso, compactación, estado, acta ---- */}
          <div className="flex flex-col gap-4">
            <InfoSection title="SECUESTRO">
              <InfoRow
                label="Sector"
                value={
                  <div className="flex items-center justify-between">
                    {detail.sector}
                    <Button
                      size="xs"
                      onClick={openModal}
                      className="ml-3"
                    >
                      Cambiar sector
                    </Button>
                  </div>
                }
              />

              <InfoRow
                label="Inventario"
                value={detail.inventario}
              />
            </InfoSection>

            <EgresoSection
              variant={egresoVariant}
              levantamiento={levantamiento}
              onInfo={() => openImagenInfo("egreso")}
              onEgresar={openModalEgreso}
              onVerEgreso={openVerEgreso}
              onVerArchivo={openURL}
            />

            <CompactarSection
              variant={compactarVariant}
              onInfo={() => openImagenInfo("compactacion")}
              onCompactar={openModalCompactar}
            />

            {mostrarCambiarEstado && (
              <InfoSection
                title="ESTADO"
                infoButton={
                  <button
                    onClick={() => openImagenInfo("cambiarEstado")}
                    aria-label="Información sobre cambio de estado"
                  >
                    <InfoIcon className="size-6" />
                  </button>
                }
                action={
                  <Button
                    size="sm"
                    onClick={openCambiarEstado}
                  >
                    Cambiar estado
                  </Button>
                }
              >
                <InfoRow
                  label="Estado actual"
                  value={estado}
                />
              </InfoSection>
            )}

            <InfoSection title="ACTA">
              <InfoRow
                label="Nro."
                value={detail.Acta?.nro}
              />

              <InfoRow
                label="Lugar"
                value={detail.Acta?.lugar}
              />

              <InfoRow
                label="Inspector"
                value={
                  detail.infoJuzgado?.inspector ??
                  detail.Acta?.inspector
                }
              />

              <InfoRow
                label="Fecha y Hora"
                value={fechaHoraFormateada ?? "Sin fecha"}
              />
            </InfoSection>
          </div>

          {/* ---- COLUMNA DERECHA: vehículo, infractor, infracciones ---- */}
          <div className="flex flex-col gap-4">
            <InfoSection title="VEHÍCULO">
              <InfoRow
                label="Dominio"
                value={detail.Vehiculo?.dominio}
              />

              <InfoRow
                label="Tipo"
                value={detail.Vehiculo?.tipovh}
              />

              <InfoRow
                label="Marca"
                value={detail.Vehiculo?.marcavh}
              />

              <InfoRow
                label="Modelo"
                value={detail.Vehiculo?.modelovh}
              />
            </InfoSection>

            <InfoSection title="INFRACTOR">
              <InfoRow
                label="Apellido y Nombres"
                value={
                  detail.Infractore?.nombreCompleto ??
                  detail.infoJuzgado?.infractor?.nombre
                }
              />

              <InfoRow
                label="DNI"
                value={
                  detail.Infractore?.dni ??
                  detail.infoJuzgado?.infractor?.dni
                }
              />

              <InfoRow
                label="Sexo"
                value={detail.Infractore?.sexo}
              />

              <InfoRow
                label="CUIL"
                value={detail.Infractore?.cuil}
              />
            </InfoSection>

            {detail.infoJuzgado?.infracciones && (
              <InfoSection title="INFRACCIÓN/ES">
                <div className="flex flex-col divide-y divide-[#61ABCF]">
                  {detail.infoJuzgado.infracciones.map((infr) => (
                    <div
                      key={infr.id ?? infr.descripcion}
                      className="px-4 py-2"
                    >
                      {infr.descripcion}
                      {infr.digesto}
                    </div>
                  ))}
                </div>
              </InfoSection >
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DetailComponent;